import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { MapPin, ArrowRight, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StateCard = ({ state }) => (
  <Link
    to={`/peptide-clinics/${state.slug}`}
    className="group flex items-center gap-4 p-5 bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 border border-gray-100 hover:border-blue-200"
    data-testid={`state-${state.slug}`}
  >
    <div className="flex-shrink-0 w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center group-hover:bg-blue-100 transition-colors">
      <span className="text-blue-600 font-bold text-sm">{state.abbr}</span>
    </div>
    <div className="flex-1 min-w-0">
      <h3 className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{state.name}</h3>
      <p className="text-sm text-gray-500 truncate">{state.cities.slice(0, 3).join(', ')}</p>
    </div>
    <ArrowRight className="text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all flex-shrink-0" size={18} />
  </Link>
);

const ClinicHub = () => {
  const [states, setStates] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const loadStates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clinic-states`);
      setStates(res.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error loading states:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStates();
  }, [loadStates]);

  const filtered = filter
    ? states.filter(s =>
        s.name.toLowerCase().includes(filter.toLowerCase()) ||
        s.abbr.toLowerCase().includes(filter.toLowerCase()) ||
        s.cities.some(c => c.toLowerCase().includes(filter.toLowerCase()))
      )
    : states;

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{'Peptide Therapy Clinics by State | Find Licensed Providers | PeptideSciences.ai'}</title>
        <meta name="description" content="Find peptide therapy clinics and licensed providers in all 50 US states. State-by-state guide to peptide treatments including BPC-157, semaglutide, CJC-1295, and more." />
        <link rel="canonical" href="https://www.peptidesciences.ai/peptide-clinics" />
        <meta property="og:title" content="Peptide Therapy Clinics by State | PeptideSciences.ai" />
        <meta property="og:description" content="Find peptide therapy clinics and licensed providers in all 50 US states." />
        <meta property="og:url" content="https://www.peptidesciences.ai/peptide-clinics" />
      </Helmet>
      <Navigation />

      <section className="bg-gradient-to-r from-gray-900 via-slate-800 to-gray-900 text-white py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{backgroundImage: 'url(https://customer-assets.emergentagent.com/job_07859234-c0aa-4e8d-94c2-c8d61ccd3354/artifacts/a25q64nh_Peptide%20Background.png)', backgroundSize: 'cover', backgroundPosition: 'center'}}></div>
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-slate-800/85 to-gray-900/90"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <MapPin size={32} className="text-blue-400" />
            <span className="text-blue-400 font-semibold uppercase tracking-wide text-sm">State-by-State Directory</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight" data-testid="clinic-hub-title">
            Peptide Therapy Clinics
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl">
            Find licensed peptide therapy providers across all 50 US states. Each state page includes local provider information, legal status, popular peptide treatments, and what to expect from your first consultation.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Search */}
        <div className="mb-10">
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by state, city, or abbreviation..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="state-search"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20 text-xl text-gray-600">Loading states...</div>
        ) : (
          <>
            <div className="mb-6 text-gray-600">{filtered.length} state{filtered.length !== 1 ? 's' : ''}</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="states-grid">
              {filtered.map(state => <StateCard key={state.slug} state={state} />)}
            </div>
          </>
        )}

        {/* CTA */}
        <div className="mt-16 bg-gradient-to-r from-green-600 to-green-700 rounded-xl p-8 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">Are You a Licensed Provider?</h2>
          <p className="text-lg mb-6 text-green-100">Join our network of peptide therapy providers and reach patients actively searching for peptide treatments.</p>
          <a
            href="https://alphabiomedlabs.com/pages/register?ref=MARKBECKER"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 bg-white text-green-700 rounded-lg font-bold hover:bg-green-50 transition-all shadow-lg"
            data-testid="provider-signup-cta"
          >
            Licensed Provider/GPO Sign Up Here
            <ArrowRight className="ml-2" size={20} />
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ClinicHub;
